import { Injectable, InjectionToken, inject } from '@angular/core';

import { Clock } from '../clock/clock';
import { IndexedDbStoreService } from './indexed-db-store.service';
import { StorageFailure } from '../telemetry/enums';

export const CONTENT_CACHE_SCOPE = new InjectionToken<() => Promise<string | null>>('Signage authenticated cache scope', {
  providedIn: 'root', factory: () => async (): Promise<null> => null
});

interface CacheEntry {
  readonly schema: 1
  readonly expiresAt: number
  readonly json: string
}

// Hashed, not stored plainly: the cache key ends up in IndexedDB and must not carry the identity itself.
export async function hashCredentialScope(region: string, identity: string): Promise<string | null> {
  if (!region || !identity || typeof crypto === 'undefined' || !crypto.subtle) {
    return null;
  }

  const bytes = new TextEncoder().encode(JSON.stringify([region, identity]));
  const digest = await crypto.subtle.digest('SHA-256', bytes);

  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

// The offline cache; keys are namespaced by a credential hash, so a re-commissioned display cannot read the old community's content.
@Injectable()
export class ScopedContentStoreService {
  private readonly database = inject(IndexedDbStoreService);
  private readonly scope = inject(CONTENT_CACHE_SCOPE);
  private readonly clock = inject(Clock);
  private readonly memory = new Map<string, CacheEntry>();
  // Bumped on every clear, so a read or write started under the previous credentials is discarded.
  private generation = 0;
  public readonly storageFailure = this.database.storageFailure;

  public async read<T>(key: string, signal?: AbortSignal): Promise<T | null> {
    const scoped = await this.key(key);

    if (!scoped || signal?.aborted) {
      return null;
    }

    const generation = this.generation;
    const persisted = await this.database.read(scoped, signal);

    // Re-checked after the await: a logout landing mid-read must not serve the old scope's content.
    if (signal?.aborted || generation !== this.generation || scoped !== await this.key(key)) {
      return null;
    }

    // Memory is the fallback where IndexedDB is unavailable, so caching still works for this session.
    const entry = persisted === undefined ? this.memory.get(scoped) : persisted;

    if (entry === undefined) {
      return null;
    }

    // Anything on the device can write this record, so a bad shape is dropped rather than trusted.
    if (!this.valid(entry)) {
      this.database.report(StorageFailure.Invalid);
      await this.database.remove(scoped, signal);
      this.memory.delete(scoped);

      return null;
    }

    if (entry.expiresAt <= this.clock.now().toEpochMilli()) {
      await this.database.remove(scoped, signal);
      this.memory.delete(scoped);

      return null;
    }

    try {
      const result: unknown = JSON.parse(entry.json);

      return result as T;
    } catch {
      this.database.report(StorageFailure.Invalid);

      return null;
    }
  }

  public async write(key: string, value: unknown, expiresAt: number, signal?: AbortSignal): Promise<void> {
    const generation = this.generation;
    const scoped = await this.key(key);

    if (!scoped || signal?.aborted || generation !== this.generation) {
      return;
    }

    try {
      const json = JSON.stringify(value);
      const entry: CacheEntry = { schema: 1, expiresAt, json };

      if (!this.valid(entry) || expiresAt <= this.clock.now().toEpochMilli()) {
        this.database.report(StorageFailure.Invalid);

        return;
      }

      if (scoped !== await this.key(key) || signal?.aborted || generation !== this.generation) {
        return;
      }

      this.memory.set(scoped, entry);

      // Oldest first — Map keeps insertion order — because nothing may grow unbounded over months of uptime.
      while (this.memory.size > 64) {
        this.memory.delete(this.memory.keys().next().value as string);
      }

      await this.database.write(scoped, entry, signal);
    } catch {
      this.database.report(StorageFailure.WriteFailed);
    }
  }

  public async clear(): Promise<void> {
    ++this.generation;
    const scope = await this.scope();
    this.memory.clear();

    if (scope) {
      await this.database.removePrefix(`content:${scope}:`);
    }
  }

  public async clearAll(): Promise<void> {
    ++this.generation;
    this.memory.clear();
    await this.database.removePrefix('content:');
  }

  private async key(key: string): Promise<string | null> {
    try {
      const scope = await this.scope();

      if (scope === null) {
        return null;
      }

      // A scope that is not a clean hash would let a crafted value escape its own key prefix.
      if (!/^[a-f0-9]{64}$/.test(scope)) {
        this.database.report(StorageFailure.Invalid);

        return null;
      }

      return `content:${scope}:${key}`;
    } catch {
      this.database.report(StorageFailure.Unavailable);

      return null;
    }
  }

  private valid(value: unknown): value is CacheEntry {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const entry = value as Partial<CacheEntry>;

    return entry.schema === 1 && typeof entry.expiresAt === 'number' && Number.isSafeInteger(entry.expiresAt)
      && typeof entry.json === 'string' && entry.json.length <= 8_000_000;
  }
}
